// El dashboard del cliente contiene la estantería visual y la lista detallada de sus proyectos.
import { useState, useEffect } from 'react';
import { db } from '../firebase/config'
import { PROYECTOS_COLLECTION } from '../utils/constants'
import { 
    collection, query, where, onSnapshot
} from 'firebase/firestore';
import { calcularVigencia } from '../utils/helpers'
import { useUser } from '../context/UserContext';

export const ClientDashboard = ({ selectedClientProfile }) => {
    const { user, userData } = useUser();
    const [projects, setProjects] = useState([]);
    const [shelfProjects, setShelfProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clientView, setClientView] = useState('shelf');
    const [modalUrl, setModalUrl] = useState(null);
    const activeClientId = selectedClientProfile?.id || user?.uid;

    useEffect(() => {
        if (!activeClientId) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        const nombreColeccion = PROYECTOS_COLLECTION === "proyectos" ? "proyectos_v2" : PROYECTOS_COLLECTION;
        const q = query(
            collection(db, nombreColeccion), 
            where("clienteId", "==", activeClientId), 
            where("estadoCliente", "in", ["Activo", "Terminado"])
        );
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const projectsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            projectsData.sort((a, b) => (b.fechaApertura?.seconds || 0) - (a.fechaApertura?.seconds || 0));
            setProjects(projectsData);
        
            const latestProjectsMap = new Map();

            projectsData.forEach(project => {
                if (project.estadoCliente === 'Terminado' && project.urlHeyzine) {
                    const serviceId = project.servicioId;
                    const existingProject = latestProjectsMap.get(serviceId);

                    if (!existingProject || (project.fechaApertura?.seconds > existingProject.fechaApertura?.seconds)) {
                        latestProjectsMap.set(serviceId, project);
                    }
                }
            });

            setShelfProjects(Array.from(latestProjectsMap.values()));
            setLoading(false);
            
        }, (error) => {
            console.error("Error fetching client projects: ", error);
            setLoading(false);
        });
        
        return () => unsubscribe();
    }, [activeClientId]);

    if (!userData) {
        return <div className="text-center py-10">Cargando perfil...</div>;
    }

    const displayName = selectedClientProfile?.planta || selectedClientProfile?.nombreCompleto || userData.nombreCompleto;

    return (
        <>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Proyectos de {displayName}</h1>
            <div className="mb-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button onClick={() => setClientView('shelf')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${clientView === 'shelf' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                        Estantería de Proyectos
                    </button>
                    <button onClick={() => setClientView('list')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${clientView === 'list' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                        Lista Detallada
                    </button>
                </nav>
            </div>
            
            {loading ? (
                <p>Cargando proyectos...</p>
            ) : projects.length === 0 ? (
                <p className="text-center text-gray-500 mt-8">No hay proyectos para mostrar en esta planta.</p>
            ) : clientView === 'shelf' ? (
                <ProjectsShelf projects={shelfProjects} onOpenModal={setModalUrl} />
            ) : (
                <ClientProjectsList projects={projects} onOpenModal={setModalUrl} />
            )}

            {modalUrl && <HeyzineViewerModal url={modalUrl} onClose={() => setModalUrl(null)} />}
        </>
    );
};

export const ClientProjectsList = ({ projects, onOpenModal }) => {
    const [activeAccordion, setActiveAccordion] = useState(null); 
    const groupedProjects = projects.reduce((acc, project) => {
        const key = project.dependencia || 'Sin Dependencia';
        if (!acc[key]) acc[key] = [];
        acc[key].push(project);
        return acc;
    }, {});

    const toggleAccordion = (key) => setActiveAccordion(activeAccordion === key ? null : key);
    const formatDate = (timestamp) => !timestamp ? '---' : new Date(timestamp.seconds * 1000).toLocaleDateString('es-MX');
    const getStatusClass = (estado) => estado === 'Activo' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800';
    
    return (
        <div className="space-y-4">
            {Object.entries(groupedProjects).map(([dependencia, projs]) => (
                <div key={dependencia} className="bg-white rounded-lg shadow overflow-hidden">
                    <button onClick={() => toggleAccordion(dependencia)} className="w-full p-4 text-left font-bold text-lg text-gray-800 flex justify-between items-center hover:bg-gray-50 transition">
                        <span>{dependencia} <span className="text-sm font-normal text-gray-500">({projs.length})</span></span>
                        <span className={`transform transition-transform duration-300 ${activeAccordion === dependencia ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {activeAccordion === dependencia && (
                        <ul className="divide-y divide-gray-200 border-t">
                            {projs.map(project => {

                                if (project.servicioNombre === "Nombre de tu servicio") {
                                    console.log("Datos del proyecto modificado:", project);
                                    console.log("fechaEmision es:", project.fechaEmision);
                                    console.log("vigenciaAnos es:", project.vigenciaAnos);
                                }

                                const vigenciaInfo = calcularVigencia(project.fechaEmision, project.vigenciaAnos);

                                return (
                                    <li key={project.id} className="p-4 md:px-6 md:py-4 flex flex-col md:flex-row md:items-center">
                                        <div className="flex-1 mb-4 md:mb-0"><div className="text-sm font-medium text-gray-900">{project.servicioNombre}</div></div>
                                        <div className="w-full md:w-40 mb-4 md:mb-0"><div className="text-sm text-gray-500 hidden md:block">{formatDate(project.fechaApertura)}</div></div>
                                        <div className="w-full md:w-48 mb-4 md:mb-0 flex flex-col gap-1 ">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full w-fit ${vigenciaInfo.colorClass}`}>
                                                {vigenciaInfo.texto}
                                            </span>
                                        </div>

                                        <div className="w-full md:w-40 mb-4 md:mb-0"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(project.estadoCliente)}`}>{project.estadoCliente}</span></div>
                                        <div className="w-full md:w-56 flex items-center space-x-4">
                                            {project.urlHeyzine && <button onClick={() => onOpenModal(project.urlHeyzine)} className="text-sm font-medium text-blue-600 hover:text-blue-900">Ver Proyecto</button>}
                                            {project.urlNotaPdf1 && <a href={project.urlNotaPdf1} download className="text-sm font-medium text-red-600 hover:text-red-900">Nota Preliminar</a>}
                                            {project.urlNotaPdf2 && <a href={project.urlNotaPdf2} download className="text-sm font-medium text-red-600 hover:text-red-900">Nota Final</a>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            ))}
        </div>
    );
};

// El visor de documentos de Heyzine que uso en el dashboard del cliente.
export const HeyzineViewerModal = ({ url, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold">Visor de Documentos</h3>
                    <button onClick={onClose} className="text-gray-600 hover:text-gray-900 text-2xl font-bold">&times;</button>
                </div>
                <div className="flex-grow">
                    <iframe src={url} title="Visor de Heyzine" className="w-full h-full" frameBorder="0" allowFullScreen></iframe>
                </div>
            </div>
        </div>
    );
};

export const ProjectsShelf = ({ projects, onOpenModal }) => {
    const finishedProjects = projects.filter(p => p.estadoCliente === 'Terminado' && p.urlHeyzine);
    const coverTemplateUrl = "https://firebasestorage.googleapis.com/v0/b/portal-evelsa.firebasestorage.app/o/portada%20Carpetas.jpeg?alt=media&token=417eb65c-1694-4efc-9e2a-55528d43a8d6";

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8">
            {finishedProjects.map(project => (
                <button key={project.id} className="group text-left focus:outline-none" onClick={() => onOpenModal(project.urlHeyzine)}>
                    <div className="relative pt-[141%] bg-gray-200 rounded-lg shadow-lg group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-1 overflow-hidden">
                        <img src={coverTemplateUrl} alt="Portada de proyecto" className="absolute inset-0 w-full h-full object-cover"/>
                        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center p-4 text-white text-center">
                            <h4 className="font-bold text-lg leading-tight">{project.clienteNombre}</h4>
                            <hr className="w-1/4 my-2 border-gray-400"/>
                            <p className="text-sm">{project.servicioNombre}</p>
                        </div>
                    </div>
                    <p className="text-sm font-semibold text-gray-700 mt-3 truncate group-hover:text-blue-600">{project.servicioNombre}</p>
                </button>
            ))}
        </div>
    );
};